import React from 'react';
import type { TemplateDefinition } from '../../../types/outreach';
import { DEMOGRAPHIC_FIELDS } from '../../../constants/outreach';
import HelpTrigger from '../../../help/components/HelpTrigger';

interface Props {
  definition: TemplateDefinition;
  onChange: (definition: TemplateDefinition) => void;
}

/** A field is in exactly one of these states — "none" means it is not collected at all. */
type FieldState = 'required' | 'optional' | 'none';

const STATE_OPTIONS: { value: FieldState; label: string; activeClass: string }[] = [
  { value: 'required', label: 'Required', activeClass: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' },
  { value: 'optional', label: 'Optional', activeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' },
  { value: 'none', label: 'Not collected', activeClass: 'bg-background-tertiary text-text-secondary dark:bg-background-dark-secondary dark:text-text-dark-secondary' },
];

const WizardStep3Demographics: React.FC<Props> = ({ definition, onChange }) => {
  const getFieldState = (fieldId: string): FieldState => {
    if (definition.demographics.required_fields.includes(fieldId)) return 'required';
    if (definition.demographics.optional_fields.includes(fieldId)) return 'optional';
    return 'none';
  };

  const setFieldState = (fieldId: string, state: FieldState) => {
    const required = new Set(definition.demographics.required_fields);
    const optional = new Set(definition.demographics.optional_fields);

    required.delete(fieldId);
    optional.delete(fieldId);
    if (state === 'required') required.add(fieldId);
    if (state === 'optional') optional.add(fieldId);

    onChange({
      ...definition,
      demographics: {
        ...definition.demographics,
        required_fields: Array.from(required),
        optional_fields: Array.from(optional)
      }
    });
  };

  const districtCollected = getFieldState('district') !== 'none';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2 flex items-center justify-center gap-2">
          Collect Demographics
          <HelpTrigger contentKey="outreach-demographics" size="sm" />
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Optionally collect information about respondents
        </p>
      </div>

      <div className="flex items-center gap-3 card p-4">
        <input
          type="checkbox"
          id="enableDemographics"
          checked={definition.demographics.enabled}
          onChange={(e) => onChange({
            ...definition,
            demographics: { ...definition.demographics, enabled: e.target.checked }
          })}
          className="rounded"
        />
        <label htmlFor="enableDemographics" className="font-medium text-text-primary dark:text-text-dark-primary cursor-pointer">
          Enable demographic information collection
        </label>
      </div>

      {definition.demographics.enabled && (
        <div className="space-y-3">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Each field is <strong>Required</strong>, <strong>Optional</strong>, or <strong>Not collected</strong>.
          </p>

          {DEMOGRAPHIC_FIELDS.map((field) => {
            const state = getFieldState(field.id);

            return (
              <div key={field.id} className={`card p-4 ${field.isSpecial ? 'bg-primary-50 dark:bg-primary-900/10' : ''}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {field.label}
                    </div>
                    {field.description && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        {field.description}
                      </div>
                    )}
                    {field.id === 'district' && (
                      <div className={`text-xs mt-1 ${districtCollected
                        ? 'text-text-tertiary dark:text-text-dark-tertiary'
                        : 'text-amber-600 dark:text-amber-400'}`}>
                        {districtCollected
                          ? 'Enables district-matched recipients (House/Senate representatives) in one-click contact.'
                          : "This is required for one-click email to respondents' representatives."}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {STATE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={state === opt.value}
                        onClick={() => setFieldState(field.id, opt.value)}
                        className={`px-3 py-1 text-sm rounded whitespace-nowrap ${
                          state === opt.value
                            ? opt.activeClass
                            : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WizardStep3Demographics;
